/**
 * PRD-066 Phase 6: Payment Failure Banner
 * Global banner displayed at top of every page when a user has an active payment failure.
 * Dismissible per session, reappears on next login.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { authFetch } from '../../services/authFetch';
import './PaymentFailureBanner.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

const PaymentFailureBanner = () => {
  const { currentUser, session } = useAuth();
  const [failure, setFailure] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const fetchFailureStatus = useCallback(async () => {
    if (!currentUser || !session?.access_token) return;

    try {
      const response = await authFetch(
        `${API_BASE}/api/billing/subscription`,
        {},
        null,
        session.access_token
      );

      if (response.ok) {
        const data = await response.json();
        if (data.payment_failure && data.payment_failure.status === 'active_grace') {
          setFailure(data.payment_failure);
        } else {
          setFailure(null);
        }
      }
    } catch (err) {
      // Silently fail - banner is non-critical
      console.warn('[PaymentFailureBanner] Failed to fetch status:', err.message);
    }
  }, [currentUser, session?.access_token]);

  useEffect(() => {
    fetchFailureStatus();
    // Re-check every 5 minutes
    const interval = setInterval(fetchFailureStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchFailureStatus]);

  // Check session storage for dismissal
  useEffect(() => {
    const dismissedKey = 'payment_failure_banner_dismissed';
    if (sessionStorage.getItem(dismissedKey) === 'true') {
      setDismissed(true);
    }
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('payment_failure_banner_dismissed', 'true');
  };

  const handleUpdatePayment = async () => {
    if (!session?.access_token) return;

    try {
      setPortalLoading(true);
      const response = await authFetch(
        `${API_BASE}/api/billing/portal`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ returnUrl: window.location.href })
        },
        null,
        session.access_token
      );

      if (response.ok) {
        const data = await response.json();
        if (data.url) {
          window.location.href = data.url;
        }
      }
    } catch (err) {
      console.error('[PaymentFailureBanner] Portal error:', err);
    } finally {
      setPortalLoading(false);
    }
  };

  // Don't render if no failure, dismissed, or no user
  // Note: Don't hide during loading/polling to avoid flash
  if (!failure || dismissed || !currentUser) {
    return null;
  }

  // Calculate urgency
  const gracePeriodEnd = new Date(failure.grace_period_ends_at);
  const now = new Date();
  const msRemaining = gracePeriodEnd - now;
  const daysRemaining = Math.max(0, Math.ceil(msRemaining / (24 * 60 * 60 * 1000)));
  const hoursRemaining = Math.max(0, Math.ceil(msRemaining / (60 * 60 * 1000)));
  const isUrgent = daysRemaining <= 7;
  const isCritical = daysRemaining <= 3;

  // Build countdown text
  let countdownText;
  if (daysRemaining <= 0) {
    countdownText = 'Grace period has expired';
  } else if (daysRemaining === 1) {
    countdownText = `${hoursRemaining} hours remaining`;
  } else {
    countdownText = `${daysRemaining} days remaining`;
  }

  return (
    <div className={`payment-failure-banner ${isCritical ? 'critical' : isUrgent ? 'urgent' : ''}`}>
      <div className="payment-failure-banner-content">
        <div className="payment-failure-banner-icon">
          {isCritical ? '\u26A0' : '\u26A0'}
        </div>
        <div className="payment-failure-banner-text">
          <strong>Payment Failed</strong>
          <span className="payment-failure-banner-sep">&mdash;</span>
          <span>
            Your payment method was declined.
            {daysRemaining > 0
              ? ` Update your payment method within ${countdownText} to avoid losing access.`
              : ' Your subscription has been downgraded. Resubscribe to restore access.'}
          </span>
          {failure.failure_count > 1 && (
            <span className="payment-failure-banner-count">
              ({failure.failure_count} failed attempts)
            </span>
          )}
        </div>
        <div className="payment-failure-banner-actions">
          <button
            className="payment-failure-banner-btn update"
            onClick={handleUpdatePayment}
            disabled={portalLoading}
          >
            {portalLoading ? 'Loading...' : 'Update Payment Method'}
          </button>
          <button
            className="payment-failure-banner-btn dismiss"
            onClick={handleDismiss}
            title="Dismiss for this session"
          >
            &times;
          </button>
        </div>
      </div>
      {daysRemaining > 0 && (
        <div className="payment-failure-banner-progress">
          <div
            className="payment-failure-banner-progress-fill"
            style={{ width: `${Math.max(0, Math.min(100, (daysRemaining / 14) * 100))}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default PaymentFailureBanner;
